import { useMemo } from 'react';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { EventsResponse } from '../types/events';
import { stableStringify } from '@Utils/searchParams';

type DefaultError = unknown;

type UseEventsParams<
  S extends Record<string, unknown> = Record<string, unknown>,
> = {
  searchParams?: S;
  enabled?: boolean;
};

export function useEvents<TData = EventsResponse, TError = DefaultError>(
  { searchParams, enabled = true }: UseEventsParams = {},
  options?: Omit<
    UseQueryOptions<EventsResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  const queryKey: QueryKey = ['events', stableParams];

  return useQuery<EventsResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => service.getEvents(searchParams ?? {}),
    ...(options as any),
  });
}
